import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Tab, Tabs } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import HubDetailsTab from './tabs/HubDetailsTab';
import HubHistoryTab from './tabs/HubHistoryTab/HubHistoryTab';
import SeamDetectionImagesTab from './tabs/SeamDetectionImagesTab';
import ActionsTab from './tabs/ActionsTab';
import AdvancedTab from './tabs/AdvancedTab';
import HubNotesTab from './tabs/HubNotesTab';
import {
  faCircleExclamation,
  faTriangleExclamation,
  faCircleCheck,
} from '@fortawesome/pro-solid-svg-icons';
import StatusChip from '../../Shared/StatusChip';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { fetchDeviceById } from '../../../store/devices/actionCreators';
import LoadingSpinner from '../../Shared/LoadingSpinner';
import { Status, UserRoles } from '../../../shared/constants';
import { capitalizeFirstLetter } from '../../../shared/utils';
import { Device } from '../../../store/devices/deviceTypes';
import AssignHubController from './AssignHubController/AssignHubController';
import {
  black,
  dimGrey,
  errorBg,
  errorRedAlt,
  inactive,
  lightBlack,
  lightPurple,
  lightTeal,
  successBg,
  successGreenAlt,
  warningBg,
  warningYellow,
} from '../../../mainTheme';

const StatusConfig = {
  [Status.Online]: {
    icon: faCircleCheck,
    color: successGreenAlt,
    text: Status.Online,
    backgroundColor: successBg,
  },
  [Status.Offline]: {
    icon: faTriangleExclamation,
    color: errorRedAlt,
    text: Status.Offline,
    backgroundColor: errorBg,
  },
  [Status.Unassigned]: {
    icon: faCircleExclamation,
    color: warningYellow,
    text: Status.Unassigned,
    backgroundColor: warningBg,
  },
} as const;

const getStatusConfig = (status: string | undefined, isUnassigned: boolean) => {
  if (isUnassigned) {
    return StatusConfig[Status.Unassigned];
  }
  if (status) {
    const normalizedStatus =
      status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    return (
      StatusConfig[normalizedStatus as keyof typeof StatusConfig] ||
      StatusConfig[Status.Offline]
    );
  }
  // Default to offline if no status
  return StatusConfig[Status.Offline];
};

const tabStyles = {
  indicator: {
    backgroundColor: lightTeal,
  },
  tab: {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    color: dimGrey,
    lineHeight: '20px',
    letterSpacing: '0.24px',
  },
  selected: {
    color: `${lightBlack} !important`,
  },
};

interface TabComponentProps {
  selectedDevice: Device | null;
  isFacilityAdmin?: boolean;
  isInternalPrivileged?: boolean;
  isEngineer?: boolean;
}

interface TabConfig {
  label: string;
  component: React.ComponentType<TabComponentProps>;
}

const tabConfig: TabConfig[] = [
  { label: 'Hub details', component: HubDetailsTab },
  { label: 'Hub history', component: HubHistoryTab },
  { label: 'Seam detection images', component: SeamDetectionImagesTab },
  { label: 'Actions', component: ActionsTab },
  { label: 'Advanced', component: AdvancedTab },
  { label: 'Notes', component: HubNotesTab },
];

interface CareHubDetailsProps {
  currentRole: string;
}

const CareHubDetails: React.FC<CareHubDetailsProps> = ({ currentRole }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [isDeviceLoading, setIsDeviceLoading] = useState(false);
  const [isAssignHubModalOpen, setIsAssignHubModalOpen] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number>();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const { selectedDevice } = useSelector((state: RootState) => state.devices);

  const isUnassigned = selectedDevice?.customer_id === null;
  const statusConfig = getStatusConfig(
    selectedDevice?.device_status,
    isUnassigned,
  );
  const isFacilityAdmin = currentRole === UserRoles.FACILITY_ADMIN;
  const isInternalPrivileged =
    currentRole === UserRoles.ENGINEERING ||
    currentRole === UserRoles.CUSTOMER_CARE;

  const isEngineer = currentRole === UserRoles.ENGINEERING;
  const filteredTabConfig = tabConfig.filter((tab) => {
    if (tab.label === 'Seam detection images') return !isFacilityAdmin;
    // Advanced = engineering sys-admin panels (channel/shadow/faults) only.
    if (tab.label === 'Advanced') return isEngineer;
    return true;
  });

  useEffect(() => {
    const loadDevice = async () => {
      if (id) {
        setIsDeviceLoading(true);
        try {
          await dispatch(fetchDeviceById(Number(id)));
        } finally {
          setIsDeviceLoading(false);
        }
      }
    };

    loadDevice();
  }, [id, dispatch]);

  const handleAssignHub = (device_id: number) => {
    setSelectedDeviceId(device_id);
    setIsAssignHubModalOpen(true);
  };

  if (isDeviceLoading || !selectedDevice) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 2,
      }}
    >
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => {
          navigate(-1);
        }}
        variant="outlined"
        color="secondary"
        sx={{
          width: '60px',
          height: '40px',
          '&:hover': {
            border: `1px solid ${inactive}`,
          },
        }}
      >
        Back
      </Button>
      <Box py={2} display="flex" flexDirection="column" gap="8px">
        <Box display="flex" justifyContent="space-between">
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '22px',
              lineHeight: '30px',
              letterSpacing: '0.36px',
              color: isUnassigned ? black : lightPurple,
            }}
          >
            {/* TODO : hyperlink the name if the profile view and url is available to develop */}
            {isUnassigned
              ? 'Unassigned'
              : selectedDevice?.first_name || selectedDevice?.last_name
                ? `${selectedDevice?.first_name} ${selectedDevice?.last_name}`
                : 'N/A'}
          </Typography>
          <StatusChip
            icon={statusConfig.icon}
            iconColor={statusConfig.color}
            labelText={statusConfig.text}
            conditionalValue={true}
            backgroundColor={statusConfig.backgroundColor}
          />
        </Box>
        {!isUnassigned && (
          <>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.2px',
                color: dimGrey,
              }}
            >
              {capitalizeFirstLetter(selectedDevice?.gender)} · Age{' '}
              {selectedDevice?.age || 'N/A'}
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.2px',
                color: lightPurple,
              }}
            >
              {selectedDevice?.email || 'N/A'} ·{' '}
              {selectedDevice?.phone || 'N/A'}
            </Typography>
          </>
        )}
        {isUnassigned && (
          <Button
            variant="contained"
            color="secondary"
            sx={{
              marginTop: 2,
              width: '132px',
              height: '40px',
            }}
            onClick={() => handleAssignHub(selectedDevice?.id)}
          >
            Assign Hub
          </Button>
        )}
      </Box>

      <Tabs
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          '& .MuiTabs-indicator': tabStyles.indicator,
          '& .MuiTab-root': tabStyles.tab,
          '& .Mui-selected': tabStyles.selected,
        }}
      >
        {filteredTabConfig.map(({ label }) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>
      <Box sx={{ borderBottom: 1, borderColor: inactive }} />
      <Box mt={2}>
        {filteredTabConfig[selectedTab]?.component &&
          React.createElement(filteredTabConfig[selectedTab].component, {
            selectedDevice: selectedDevice || null,
            isFacilityAdmin,
            isInternalPrivileged,
            isEngineer,
          })}
      </Box>

      {isAssignHubModalOpen && (
        <AssignHubController
          onClose={() => setIsAssignHubModalOpen(false)}
          device_id={selectedDeviceId || 0}
          selectedDeviceDetails={selectedDevice}
        />
      )}
    </Box>
  );
};

export default CareHubDetails;
